const STORAGE_KEY = 'blv-theme'
const THEME_EVENT = 'blv:theme-change'
const VALID_THEMES = new Set(['light', 'dark'])

function canUseDom () {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function isThemeLocked () {
  return canUseDom() && document.documentElement.dataset.themeLock === 'light'
}

function readStoredTheme () {
  if (!canUseDom()) return 'light'

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return VALID_THEMES.has(stored) ? stored : 'light'
  } catch {
    return 'light'
  }
}

export function resolveTheme (preference = readStoredTheme()) {
  if (isThemeLocked()) return 'light'
  return preference === 'dark' ? 'dark' : 'light'
}

export function getThemePreference () {
  if (isThemeLocked()) return 'light'
  return readStoredTheme()
}

export function getResolvedTheme () {
  if (!canUseDom()) return 'light'
  return document.documentElement.dataset.theme || resolveTheme()
}

export function applyTheme (preference = readStoredTheme(), { persist = false } = {}) {
  const locked = isThemeLocked()
  const nextPreference = locked ? 'light' : (VALID_THEMES.has(preference) ? preference : 'light')
  const resolved = resolveTheme(nextPreference)

  if (canUseDom()) {
    document.documentElement.dataset.theme = resolved
    document.documentElement.style.colorScheme = resolved
    document.documentElement.dataset.themePreference = nextPreference

    if (persist && !locked) {
      try {
        window.localStorage.setItem(STORAGE_KEY, nextPreference)
      } catch {
        // Private browsing and storage-disabled browsers still get theming.
      }
    }

    if (!locked) {
      window.dispatchEvent(new CustomEvent(THEME_EVENT, {
        detail: { preference: nextPreference, theme: resolved }
      }))
    }
  }

  return resolved
}

export function setThemePreference (preference) {
  return applyTheme(preference, { persist: true })
}

export function cycleThemePreference () {
  const current = getThemePreference()
  const next = current === 'dark' ? 'light' : 'dark'
  setThemePreference(next)
  return next
}

export function subscribeToTheme (callback) {
  if (!canUseDom()) return () => {}

  const handler = (event) => callback(event.detail)
  window.addEventListener(THEME_EVENT, handler)
  return () => window.removeEventListener(THEME_EVENT, handler)
}

export function initializeTheme () {
  return applyTheme(readStoredTheme())
}

if (canUseDom()) {
  initializeTheme()
}

